import React, { useState, useEffect } from 'react';
import ChatInterface from './ChatInterface';
import KnowledgeBaseManager from './KnowledgeBaseManager';
import { ChatMessage, MessageSender, URLGroup } from '../types';
import { generateContentWithUrlContext, getInitialSuggestions } from '../services/geminiService';

export function KnowledgeChatDashboard({ clienteId }: { clienteId: string }) {
  const [urlGroups, setUrlGroups] = useState<URLGroup[]>([
    { id: 'default', name: 'Documentação Principal', urls: [] }
  ]);
  const [activeUrlGroupId, setActiveUrlGroupId] = useState('default');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const activeGroup = urlGroups.find(g => g.id === activeUrlGroupId);
  const currentUrls = activeGroup ? activeGroup.urls : [];

  const fetchSuggestions = async (urlsToUse: string[]) => {
    if (urlsToUse.length === 0) {
      setSuggestions([]);
      return;
    }
    setIsFetchingSuggestions(true);
    try {
      // Já vem separado pelo serviço. A versão anterior fazia `JSON.parse` aqui
      // dentro do `try`, e uma resposta do modelo com uma vírgula a mais apagava
      // a lista inteira sem nenhum sintoma na tela.
      setSuggestions(await getInitialSuggestions(clienteId, urlsToUse));
    } catch (e) {
      console.error(e);
      setSuggestions([]);
    } finally {
      setIsFetchingSuggestions(false);
    }
  };

  useEffect(() => {
    fetchSuggestions(currentUrls);
  }, [currentUrls]);

  const handleAddUrl = (url: string) => {
    setUrlGroups(prev => prev.map(g => g.id === activeUrlGroupId ? { ...g, urls: [...g.urls, url] } : g));
  };

  const handleRemoveUrl = (url: string) => {
    setUrlGroups(prev => prev.map(g => g.id === activeUrlGroupId ? { ...g, urls: g.urls.filter(u => u !== url) } : g));
  };

  const handleSendMessage = async (query: string) => {
    const userMessage: ChatMessage = {
      text: query,
      sender: MessageSender.USER,
      timestamp: new Date()
    };
    
    const aiMessage: ChatMessage = {
      text: '',
      sender: MessageSender.MODEL,
      timestamp: new Date(),
      isLoading: true
    };
    
    setMessages(prev => [...prev, userMessage, aiMessage]);
    setIsLoading(true);
    
    try {
      const response = await generateContentWithUrlContext(clienteId, query, currentUrls);
      
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMsg = newMessages[newMessages.length - 1];
        if (lastMsg.sender === MessageSender.MODEL) {
          lastMsg.text = response.text;
          lastMsg.isLoading = false;
          lastMsg.urlContext = response.urlContextMetadata;
        }
        return newMessages;
      });
    } catch (error: any) {
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMsg = newMessages[newMessages.length - 1];
        if (lastMsg.sender === MessageSender.MODEL) {
          lastMsg.text = 'Error: ' + error.message;
          lastMsg.isLoading = false;
        }
        return newMessages;
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-[800px] w-full rounded-xl overflow-hidden bg-[#121212]">
      {/* Sidebar - URLs */}
      <div className={`w-80 h-full border-r border-[#333] transition-all duration-300 ${isSidebarOpen ? 'block' : 'hidden'}`}>
        <KnowledgeBaseManager 
          urls={currentUrls}
          urlGroups={urlGroups}
          activeUrlGroupId={activeUrlGroupId}
          onSetGroupId={setActiveUrlGroupId}
          onAddUrl={handleAddUrl}
          onRemoveUrl={handleRemoveUrl}
          onCloseSidebar={() => setIsSidebarOpen(false)}
        />
      </div>
      
      {/* Main Chat */}
      <div className="flex-1 h-full">
        <ChatInterface 
          messages={messages}
          onSendMessage={handleSendMessage}
          isLoading={isLoading}
          isFetchingSuggestions={isFetchingSuggestions}
          initialQuerySuggestions={suggestions}
          onSuggestedQueryClick={handleSendMessage}
          onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
          placeholderText="Converse com a documentação do cliente via Gemini"
        />
      </div>
    </div>
  );
}
